import * as d3 from 'd3';
import {
  coreFor,
  rimNodes,
  addTail,
  addStalk,
  addCilia,
} from './microbeShapes';

/**
 * Creature assembly: a core solid, plus whatever appendages the node budget leaves
 * over.
 *
 * Deterministic given a seed, but the seed is not fixed — the essay draws a fresh one
 * per visit, so the evolution runs through a different sequence of solids each time
 * while remaining perfectly reproducible for anyone debugging one.
 */

/**
 * Body radius in pixels, by node count.
 *
 * Plans come out of the shape library normalised to roughly 0..1.5, so they are
 * rescaled here rather than there. A creature has to grow with its node count or a
 * 900-node honeycomb packs into the same disc as a tetrahedron, but it cannot grow
 * linearly either or it leaves the stage — hence the fourth root.
 *
 * The ceiling has to leave room for the breathing pulse on top: at 328 the largest
 * body swells to 371 at the top of its breath, which still clears the 400 half-stage.
 */
const bodyRadius = (count) => 88 + 240 * Math.min(1, (count / 900) ** 0.4);

/**
 * Node radii shrink as the body gets crowded. At 900 nodes inside a 324px disc there
 * is about 17px of centre-to-centre room, so the largest accent has to stay under it.
 */
const sizeScale = (count) => Math.max(0.3, Math.min(1, Math.sqrt(150 / count)));

const mulberry32 = (seed) => {
  let a = seed >>> 0;
  return () => {
    a += 0x6d2b79f5;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

export const DEFAULT_GENOME = {
  hue0: 168,
  hueStep: 34,
  sizeExp: 1,
  symmetry: 6,
  seed: 7,
};

export const randomSeed = () => Math.floor(Math.random() * 0x7fffffff);

/**
 * Node styling from how far out the node sits, rather than from a ring index — the
 * bodies are no longer all concentric, so depth is the only thing every plan shares.
 *
 * Size is deliberately multi-modal rather than a smooth gradient. A body where every
 * node is within 20% of its neighbours reads as texture; a body with a few obvious
 * hubs, a majority of mid nodes and a scattering of specks reads as anatomy. The
 * accent multiplier does that — 6% of nodes come out enlarged, another 12% a little
 * enlarged, and the top of the jitter range is pared back to specks.
 *
 * The whole range is deliberately restrained. An earlier version ran the centre up to
 * double size, which drowned the thing the body is actually for: a hub wide enough to
 * swallow its own ring hides the geometry it sits in. The spread now comes mostly
 * from the small end, where it costs the figure nothing.
 */
export const styleOf = (depth, genome, jitter = 0.5, limb = false, scale = 1) => {
  const g = { ...DEFAULT_GENOME, ...genome };
  const t = Math.max(0, Math.min(1, depth));
  const base = 2.2 + 7.4 * (1 - t) ** 1.6 * g.sizeExp;
  const accent = jitter < 0.06 ? 1.5 : jitter < 0.18 ? 1.2 : jitter > 0.9 ? 0.55 : 1;
  return {
    r: Math.max(0.9, base * accent * (limb ? 0.66 : 1) * scale),
    color: d3
      .hsl((g.hue0 + t * g.hueStep * 3 + (limb ? 24 : 0) + jitter * 12) % 360, 0.54, 0.63)
      .formatHex(),
  };
};

/**
 * Builds a creature of roughly `count` nodes.
 *
 * The core is chosen from the plans that can use at least half the budget, so the
 * body is mostly solid and only partly limbs. Whatever is left over goes to tails,
 * flower stalks and cilia — with the split itself randomised, so two creatures of the
 * same size and the same core still come out different.
 */
export const buildBody = (count, genome = {}) => {
  const g = { ...DEFAULT_GENOME, ...genome };
  const rnd = mulberry32(g.seed);
  const budget = Math.max(1, Math.round(count));

  if (budget === 1) {
    return {
      name: 'single cell',
      nodes: [{ id: 0, restR: 0, angle: 0, phase: 0, ...styleOf(0, g, 0.5) }],
      edges: [],
    };
  }
  if (budget < 4) {
    const nodes = [];
    const edges = [];
    for (let i = 0; i < budget; i += 1) {
      nodes.push({ restR: i === 0 ? 0 : 0.5 + i * 0.2, angle: i * 2.1 });
      if (i) edges.push({ source: i - 1, target: i, kind: 'limb' });
    }
    return finish({ nodes, edges, name: 'chain' }, g, rnd);
  }

  const pick = coreFor(budget, rnd);
  const core = pick ? pick.make() : { nodes: [], edges: [], name: 'chain' };
  const body = {
    name: core.name,
    nodes: core.nodes.map((n) => ({ ...n })),
    edges: core.edges.map((e) => ({ ...e })),
  };

  // Appendages are capped at a share of the budget. Unbounded, a 900-node creature
  // spends 400 nodes on cilia and stops being a solid with limbs.
  const maxLimbs = Math.max(2, Math.round(budget * 0.22));
  let spare = Math.min(budget - body.nodes.length, maxLimbs);

  // Appendages, in descending order of how much they cost, so a small remainder
  // still produces something rather than being dropped.
  while (spare > 0) {
    const roll = rnd();
    if (spare >= 4 && roll < 0.38) {
      const petals = 2 + Math.floor(rnd() * 3);
      const stem = 1 + Math.floor(rnd() * 2);
      const cost = petals + stem;
      if (cost > spare) {
        spare -= 1;
        addCilia(body, rimNodes(body, 1, rnd), rnd);
        continue;
      }
      addStalk(body, rimNodes(body, 1, rnd)[0], stem, petals, rnd);
      spare -= cost;
    } else if (spare >= 2 && roll < 0.72) {
      const len = Math.min(spare, 2 + Math.floor(rnd() * 3));
      addTail(body, rimNodes(body, 1, rnd)[0], len, rnd);
      spare -= len;
    } else {
      const n = Math.min(spare, 1 + Math.floor(rnd() * 3));
      addCilia(body, rimNodes(body, n, rnd), rnd);
      spare -= n;
    }
  }

  return finish(body, g, rnd);
};

/** Scale shells to pixels and attach per-node styling and pulse phase. */
const finish = (body, genome, rnd) => {
  const maxR = Math.max(0.001, d3.max(body.nodes, (n) => n.restR) || 0.001);
  const count = body.nodes.length;
  const radius = bodyRadius(count);
  const scale = sizeScale(count);

  const nodes = body.nodes.map((n, i) => ({
    id: i,
    // Normalised by the plan's own outermost shell, so every plan fills its stage.
    restR: (n.restR / maxR) * radius,
    angle: n.angle,
    limb: Boolean(n.limb),
    // Golden angle, so the breathing pulse travels around the body as a wave
    // rather than every node inflating on the same beat.
    phase: i * 2.399963,
    ...styleOf(n.restR / maxR, genome, rnd(), n.limb, scale),
  }));

  /**
   * Rest length per edge, measured off the designed layout rather than taken from a
   * constant per edge kind.
   *
   * Constants were the reason bodies came out clenched. A rosette's shells can sit 50
   * or 60px apart while every spoke insisted on being 30px long, so the link force
   * spent the whole time hauling the shells inward against the shape force holding
   * them out. The plan already knows exactly how far apart two joined nodes should
   * be — so ask it.
   */
  /**
   * Designate a head — the outermost core node — and make it the largest in the body.
   *
   * Swimming is steered by whichever node is biggest, and left to the size gradient
   * that is almost always the *centre*, which cannot lead anything: it sits at radius
   * zero, so no rotation can ever put it out front. Promoting a rim node instead
   * gives the creature a nose, and the orientation solve then has something real to
   * point along the heading.
   */
  if (nodes.length >= 3) {
    let headIdx = 0;
    let far = -Infinity;
    nodes.forEach((n, i) => {
      if (n.limb) return;
      if (n.restR > far) {
        far = n.restR;
        headIdx = i;
      }
    });
    const other = nodes.reduce((mx, n, i) => (i === headIdx ? mx : Math.max(mx, n.r)), 1);
    nodes[headIdx].r = other * 1.35;
    nodes[headIdx].head = true;
  }

  const px = nodes.map((n) => [Math.cos(n.angle) * n.restR, Math.sin(n.angle) * n.restR]);

  return {
    name: body.name,
    nodes,
    edges: body.edges.map((e) => ({
      ...e,
      dist: Math.max(6, Math.hypot(px[e.source][0] - px[e.target][0], px[e.source][1] - px[e.target][1])),
    })),
  };
};

/**
 * The scroll: a sequence of whole creatures, not one creature growing a tail.
 *
 * Each stage is rebuilt from scratch at a larger node budget, which lets the core
 * plan change — tetrahedron, cube, octahedron, icosahedron, dodecahedron, lattice,
 * rosette. Node objects survive the change and only their roles are reassigned, so
 * what the reader sees is the body reorganising itself rather than a cut.
 */
const STAGE_COUNTS = [
  1, 4, 7, 12, 19, 30, 46, 70, 105, 155, 225, 320, 450, 620, 800, 940,
];

export const STAGE_COUNT = STAGE_COUNTS.length;

export const buildStages = (seed) => {
  const out = [];
  let prev = 0;
  STAGE_COUNTS.forEach((count, i) => {
    const genome = {
      ...DEFAULT_GENOME,
      seed: (seed + i * 7919) >>> 0,
      hue0: (168 + i * 26) % 360,
      symmetry: 5 + (i % 4),
      sizeExp: 0.85 + (i % 3) * 0.12,
    };
    // Growth has to be monotonic or the creature shrinks mid-scroll. A plan is only
    // ever an approximate fit for a budget, so ask again with a larger one until the
    // result actually exceeds the stage before it.
    let body = buildBody(count, genome);
    for (let attempt = 1; attempt < 10 && body.nodes.length <= prev; attempt += 1) {
      body = buildBody(Math.round(count * (1 + attempt * 0.09)), genome);
    }
    prev = body.nodes.length;
    out.push(body);
  });
  return out;
};

/** Stage index for a scroll position, eased so the early solids get dwell time. */
export const stageAt = (p) => {
  const q = Math.max(0, Math.min(1, p));
  return Math.min(STAGE_COUNT - 1, Math.floor(q ** 1.25 * STAGE_COUNT));
};

/**
 * How two creatures' genomes combine when one absorbs the other.
 *
 * Deterministic in the merge order, which is the point: the same sequence of
 * collisions always produces the same finished creature, and a different sequence
 * produces a visibly different one. Hue is size-weighted so a big creature keeps its
 * character, then twisted by the golden angle each merge so successive absorptions
 * never land on the same colour. Whether the absorbed creature was larger than its
 * absorber pushes the size exponent and the symmetry in opposite directions — and
 * since symmetry and seed both feed the core choice, eating upward and eating
 * downward genuinely end on different solids rather than on different palettes.
 */
export const mergeGenomes = (a, b, sizeA, sizeB, mergeIndex) => {
  const total = sizeA + sizeB || 1;
  const swallowedUp = sizeB > sizeA;
  return {
    hue0: ((a.hue0 * sizeA + b.hue0 * sizeB) / total + 137.508 * (mergeIndex + 1)) % 360,
    hueStep: (a.hueStep + b.hueStep) / 2 + (swallowedUp ? 6 : -4),
    sizeExp: Math.max(
      0.55,
      Math.min(1.6, (a.sizeExp + b.sizeExp) / 2 + (swallowedUp ? 0.12 : -0.08))
    ),
    symmetry: swallowedUp ? Math.min(10, a.symmetry + 1) : Math.max(4, a.symmetry - 1),
    seed: (a.seed * 31 + b.seed * 17 + mergeIndex * 2654435761) >>> 0,
  };
};
